import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { paymentsApi } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Shield, CheckCircle, Loader2, XCircle } from 'lucide-react';

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { fetchUser } = useAuth();
  const [status, setStatus] = useState('loading');
  const [attempts, setAttempts] = useState(0);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (attempts >= 5) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await paymentsApi.getStatus(sessionId);
      
      if (response.payment_status === 'paid') {
        setStatus('success');
        // Refresh user data to get updated subscription
        await fetchUser();
      } else if (response.status === 'expired') {
        setStatus('expired');
      } else {
        // Continue polling
        setAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    } catch (err) {
      console.error('Payment status check failed:', err);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50/30 flex flex-col items-center justify-center px-4">
      <div className="flex items-center gap-2 mb-8">
        <Shield className="h-10 w-10 text-teal-600" />
        <span className="font-bold text-2xl text-slate-900" style={{ fontFamily: 'Manrope' }}>
          AICreditPilot
        </span>
      </div>

      <Card className="w-full max-w-md border-slate-200 shadow-xl" data-testid="payment-status-card">
        <CardContent className="pt-8 pb-8 text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="h-16 w-16 mx-auto mb-4 text-teal-600 animate-spin" />
              <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
                Processing Payment
              </h1>
              <p className="text-slate-600">
                Please wait while we confirm your payment...
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
                Payment Successful!
              </h1>
              <p className="text-slate-600 mb-6">
                Welcome to Premium! Your automatic dispute sending is now active.
              </p>
              <Button 
                className="btn-teal w-full"
                onClick={() => navigate('/dashboard')}
                data-testid="go-dashboard-btn"
              >
                Go to Dashboard
              </Button>
            </>
          )}

          {status === 'expired' && (
            <>
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
                <XCircle className="h-10 w-10 text-amber-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
                Session Expired
              </h1>
              <p className="text-slate-600 mb-6">
                Your payment session has expired. Please try again.
              </p>
              <Button 
                className="btn-teal w-full"
                onClick={() => navigate('/pricing')}
                data-testid="try-again-btn"
              >
                Try Again
              </Button>
            </>
          )}

          {(status === 'error' || status === 'timeout') && (
            <>
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                <XCircle className="h-10 w-10 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
                Something Went Wrong
              </h1>
              <p className="text-slate-600 mb-6">
                {status === 'timeout' 
                  ? 'Payment verification timed out. Please check your email for confirmation.'
                  : 'There was an issue processing your payment.'}
              </p>
              <div className="space-y-3">
                <Button 
                  className="btn-teal w-full"
                  onClick={() => navigate('/dashboard')}
                  data-testid="go-dashboard-btn"
                >
                  Go to Dashboard
                </Button>
                <Button 
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate('/pricing')}
                  data-testid="retry-btn"
                >
                  Try Again
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
